// components/AddMemberModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, X } from '@/components/ui/lucide-memo';
import SimpleLoader from './ui/SimpleLoader';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { email: string }) => Promise<void>;
  onCreateDirect: (data: { email: string; firstName: string; lastName: string }) => Promise<string>;
  isLoading: boolean;
  isCreating: boolean;
  initialEmail?: string;
}

const inputClass =
  'h-10 w-full rounded-[12px] border border-[#2F2F2F] bg-transparent px-3 !text-[15px] text-[#ECECEC] outline-none placeholder:text-[#8E8E8E] focus:border-[#8E8E8E] disabled:cursor-not-allowed disabled:opacity-50';

const secondaryButtonClass =
  'inline-flex h-9 items-center justify-center rounded-full px-4 !text-[15px] text-[#ECECEC] transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40';

const primaryButtonClass =
  'inline-flex h-9 min-w-24 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#F0F0F0] px-4 !text-[15px] font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onCreateDirect,
  isLoading,
  isCreating,
  initialEmail,
}) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setInviteEmail('');
    setCreateEmail(initialEmail || '');
    setFirstName('');
    setLastName('');
    setInviteError(null);
    setCreateError(null);
    setCreatedPassword(null);
    setCopied(false);
  }, [initialEmail, isOpen]);

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleInviteEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInviteEmail(event.target.value);
  }, []);

  const handleCreateEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateEmail(event.target.value);
  }, []);

  const handleFirstNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value.replace(/[^a-zA-Z]/g, ''));
  }, []);

  const handleLastNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value.replace(/[^a-zA-Z]/g, ''));
  }, []);

  const handleInviteSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setInviteError(null);
    if (!inviteEmail.trim()) {
      setInviteError('Email address is required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(inviteEmail)) {
      setInviteError('Please enter a valid email address.');
      return;
    }
    void onSubmit({ email: inviteEmail });
  };

  const handleCreateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreateError(null);
    if (!createEmail.trim() || !firstName.trim() || !lastName.trim()) {
      setCreateError('All fields are required for direct creation.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(createEmail)) {
      setCreateError('Please enter a valid email address.');
      return;
    }
    try {
      const password = await onCreateDirect({ email: createEmail, firstName, lastName });
      setCreatedPassword(password);
    } catch {
      // The parent surfaces the error and keeps this form open for correction.
    }
  };

  const signInUrl = `${process.env.NEXT_PUBLIC_SITE_URL || 'https://app.fater.ai'}/sign-in`;
  const credentials = `${signInUrl}\nEmail: ${createEmail}\nPassword: ${createdPassword || ''}`;

  const handleCopy = () => {
    if (!createdPassword || !createEmail) return;
    void navigator.clipboard.writeText(credentials);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  const isProcessing = isLoading || isCreating;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl"
        onClick={stopPropagation}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"
          aria-label="Close modal"
          disabled={isProcessing}
        >
          <X size={17} />
        </button>

        {createdPassword ? (
          <div className="p-5">
            <h2 className="text-[18px] font-medium text-[#ECECEC]">Member created</h2>
            <p className="mt-1 text-sm text-white/45">
              Copy these credentials and share them securely with the new member.
            </p>

            <div className="relative mt-4 rounded-[16px] bg-[#1B1B1B] p-3 pr-11">
              <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-5 text-white/70">
                {credentials}
              </pre>
              <button
                type="button"
                onClick={handleCopy}
                title="Copy credentials"
                className="absolute right-2 top-2 rounded-full p-1.5 text-white/45 transition-colors hover:bg-white/[0.08] hover:text-white/80"
              >
                {copied ? <Check size={15} className="text-[#4DAB9A]" /> : <Copy size={15} />}
              </button>
            </div>

            <p className="mt-2 text-xs text-white/35">
              They will be asked to change this password after signing in.
            </p>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className={primaryButtonClass}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <header className="border-b border-white/[0.055] px-5 py-4 pr-12">
              <h2 className="text-[18px] font-medium text-[#ECECEC]">Add a member</h2>
              <p className="mt-1 text-sm text-white/45">
                Invite someone by email or create their account directly.
              </p>
            </header>

            <div className="space-y-5 p-5">
              <section>
                <h3 className="text-sm font-medium text-white/80">Send an invitation</h3>
                <p className="mt-0.5 text-xs text-white/40">
                  They will receive instructions to join the organisation.
                </p>
                <form onSubmit={handleInviteSubmit} className="mt-3">
                  <label htmlFor="invite-email" className="mb-1 block text-xs font-medium text-white/55">
                    Email address
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="email"
                      id="invite-email"
                      value={inviteEmail}
                      onChange={handleInviteEmailChange}
                      className={inputClass}
                      placeholder="user@example.com"
                      required
                      disabled={isProcessing}
                    />
                    <button type="submit" className={primaryButtonClass} disabled={isProcessing}>
                      {isLoading ? <SimpleLoader size={12} message="" /> : 'Send invite'}
                    </button>
                  </div>
                  {inviteError && <p className="mt-1.5 text-xs text-[#FF7369]">{inviteError}</p>}
                </form>
              </section>

              <section className="border-t border-white/[0.055] pt-5">
                <h3 className="text-sm font-medium text-white/80">Create an account directly</h3>
                <p className="mt-0.5 text-xs text-white/40">
                  This skips the email invitation and creates a temporary password.
                </p>
                <form onSubmit={handleCreateSubmit} className="mt-3 space-y-3">
                  <div>
                    <label htmlFor="create-email" className="mb-1 block text-xs font-medium text-white/55">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="create-email"
                      value={createEmail}
                      onChange={handleCreateEmailChange}
                      className={inputClass}
                      placeholder="user@example.com"
                      required
                      disabled={isProcessing}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="firstName" className="mb-1 block text-xs font-medium text-white/55">
                        First name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={handleFirstNameChange}
                        className={inputClass}
                        placeholder="John"
                        required
                        disabled={isProcessing}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="mb-1 block text-xs font-medium text-white/55">
                        Last name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={handleLastNameChange}
                        className={inputClass}
                        placeholder="Doe"
                        required
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                  {createError && <p className="text-xs text-[#FF7369]">{createError}</p>}
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose} className={secondaryButtonClass} disabled={isProcessing}>
                      Cancel
                    </button>
                    <button type="submit" className={primaryButtonClass} disabled={isProcessing}>
                      {isCreating ? <SimpleLoader size={12} message="" /> : 'Create member'}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddMemberModal;
